import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PACKAGING_CATEGORY_LABELS_IT,
  type PackagingCategory,
} from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@produzionejb/ui';
import { useDeletePackagingItem, usePackagingItems } from '../../lib/packaging-items-api';

const PAGE_SIZE = 100;

export function PackagingItemListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState<PackagingCategory | ''>('');
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(1);

  function handleSearchChange(v: string) {
    setSearch(v);
    setPage(1);
    window.clearTimeout((handleSearchChange as unknown as { t: number }).t);
    (handleSearchChange as unknown as { t: number }).t = window.setTimeout(() => {
      setDebouncedSearch(v.trim());
    }, 300);
  }

  const { data, isLoading, error } = usePackagingItems({
    page,
    pageSize: PAGE_SIZE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(category && { category }),
    ...(activeFilter && { active: activeFilter === 'true' }),
  });

  const deleteMut = useDeletePackagingItem();

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(`Eliminare il packaging "${name}"? Operazione irreversibile.`)) return;
    try {
      await deleteMut.mutateAsync(id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">Packaging</h2>
        <Button onClick={() => navigate('/packaging-items/new')}>Nuovo packaging</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtri</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-4">
          <div>
            <label className="text-xs text-slate-500">Cerca</label>
            <Input
              placeholder="Codice o nome..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Categoria</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value as PackagingCategory | '');
                setPage(1);
              }}
            >
              <option value="">Tutte</option>
              {Object.entries(PACKAGING_CATEGORY_LABELS_IT).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Stato</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value as '' | 'true' | 'false');
                setPage(1);
              }}
            >
              <option value="">Tutti</option>
              <option value="true">Attivi</option>
              <option value="false">Disattivati</option>
            </select>
          </div>
          <div className="flex items-end">
            <span className="text-sm text-slate-600">
              {data ? `${data.total} risultati` : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading && <p className="p-4 text-sm text-slate-500">Caricamento...</p>}
          {error && (
            <p className="p-4 text-sm text-red-600">
              Errore: {(error as Error).message}
            </p>
          )}
          {data && data.items.length === 0 && (
            <p className="p-4 text-sm text-slate-500">Nessun packaging trovato.</p>
          )}
          {data && data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-4 py-2">Codice</th>
                  <th className="px-4 py-2">Nome</th>
                  <th className="px-4 py-2">Categoria</th>
                  <th className="px-4 py-2">UM</th>
                  <th className="px-4 py-2 text-right">Stock</th>
                  <th className="px-4 py-2">Stato</th>
                  <th className="px-4 py-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs">{p.code}</td>
                    <td className="px-4 py-2 font-medium text-slate-900">{p.name}</td>
                    <td className="px-4 py-2 text-slate-700">
                      {PACKAGING_CATEGORY_LABELS_IT[p.category]}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{p.unit}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{p.residualQty}</td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          p.active
                            ? 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'
                            : 'rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-600'
                        }
                      >
                        {p.active ? 'Attivo' : 'Disattivato'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to={`/packaging-items/${p.id}/edit`}
                        className="mr-3 text-brand-700 hover:underline"
                      >
                        Modifica
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id, p.name)}
                        className="text-red-600 hover:underline"
                        disabled={deleteMut.isPending}
                      >
                        Elimina
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </Button>
          <span className="text-sm text-slate-600">
            Pagina {page} di {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </Button>
        </div>
      )}
    </div>
  );
}
